import type { StatusRole } from '@lighthouse/design'
import type {
  ConfigurationCrossHostCheck, ConfigurationCrossHostCheckKind, ConfigurationHost,
  ConfigurationHostRole, ConfigurationObservation, ConfigurationSetting, ConfigurationSourceKind,
  ConfigurationTier,
} from '../../types/observatory'

/**
 * Presentation for the configuration page.
 *
 * Every helper here is a wording or layout decision, never a disclosure one.
 * `GET api/observatory/configuration` has already decided what may be said about
 * each setting: which keys exist at all, which tier each is in, whether a value
 * may appear, and the exact text of every cell (`valueLabel`). **Nothing in this
 * file formats a value.** A helper that built a cell's text out of `value` would
 * be a second place the page decides what to show, and the server's rules —
 * presence only for Bootstrap, withheld when a secret store supplied it, withheld
 * when it does not re-parse — would then hold only as long as the two copies
 * agreed. The one read of `value` anywhere is {@link apiRunsInDevelopment}, which
 * compares a served Locked value to choose a banner and prints nothing of it.
 *
 * The cross-host verdicts (`crossHostCheck.holds`) are evaluated server-side too,
 * over hosts reporting now. They are rendered, never re-derived.
 */

/**
 * The settings doc 16 §7 found silently off, which the page leads with.
 *
 * **The one list of keys this app holds, and it decides ORDER only.** It cannot
 * hide anything: every setting is also in the full, server-ordered list below it,
 * so a key missing here costs a headline and never a reading. And it cannot go
 * quietly stale in the direction that matters — a key named here that the server
 * no longer serves is rendered as a visible "not served" row rather than dropped,
 * so a rename on the allowlist breaks this panel out loud.
 *
 * Why a list at all, when the ETL job catalogue and the audit categories both
 * keep their lists server-side: those are MEMBERSHIP decisions — a ninth
 * governance operation silently missing from a filter is a lie about the trail.
 * This is an editorial one about which rows to read first, lifted verbatim from
 * doc 16 §7's table and its closing sentence, and every row it names is served
 * in full either way.
 */
export const SILENTLY_OFF_KEYS: readonly string[] = [
  'DataQuality:Enabled',
  'Notifications:Enabled',
  'Cartographer:Seal:EnableRowLevelSecurity',
  'Jobs:FullEtl:Enabled',
  'Jobs:IncrementalEtl:Enabled',
]

/** The tiers in page order: Locked first, because doc 16 §5 calls it the tier that earns the page. */
export const TIER_ORDER: readonly ConfigurationTier[] = ['Locked', 'Operational', 'Bootstrap']

/** One column of the per-host matrix: one host process, in the server's order. */
export interface HostColumn {
  id: string
  host: ConfigurationHost
}

/**
 * The matrix's columns, one per served host, in the server's order.
 *
 * Built from `hosts` rather than from the observations, so a host that binds a
 * setting and has reported nothing still gets a column — and a column of dashes
 * is how "Automaton has never published" looks on the page, rather than the
 * column quietly not existing.
 */
export function hostColumns(hosts: ConfigurationHost[]): HostColumn[] {
  return hosts.map((host) => ({ id: hostKey(host.role, host.hostName), host }))
}

function hostKey(role: ConfigurationHostRole, hostName: string | null): string {
  // Machine names compare case-insensitively, as the server's own grouping does.
  return `${role}::${(hostName ?? '').toLowerCase()}`
}

/**
 * This column's reading of this setting, or `undefined` when the host does not
 * bind the key at all.
 *
 * `undefined` is a third state and must not render like either of the other two.
 * The server emits an observation for every host that binds a setting — a
 * never-reported one included — so an absent entry means the key is not read on
 * that host: "the API has no opinion on DataQuality", which is neither "off"
 * nor "unmeasured".
 */
export function observationFor(
  setting: ConfigurationSetting,
  column: HostColumn,
): ConfigurationObservation | undefined {
  return setting.observations.find((o) => hostKey(o.hostRole, o.hostName) === column.id)
}

/**
 * Observations no column claims. Expected to be empty, always.
 *
 * Every observation's host should appear in `hosts`, and today it does. This
 * exists so that if the two ever drift, the reading is printed under its setting
 * instead of vanishing — on a page whose job is to stop settings being invisible,
 * a matrix that silently drops a cell would be the defect it was built to remove.
 */
export function unplacedObservations(
  setting: ConfigurationSetting,
  columns: HostColumn[],
): ConfigurationObservation[] {
  const ids = new Set(columns.map((c) => c.id))
  return setting.observations.filter((o) => !ids.has(hostKey(o.hostRole, o.hostName)))
}

/** The hosts on the page that do not read this key. */
export function unboundColumns(setting: ConfigurationSetting, columns: HostColumn[]): HostColumn[] {
  return columns.filter((column) => observationFor(setting, column) === undefined)
}

/** "API", "Automaton", "Hangfire dashboard". */
export function roleLabel(role: ConfigurationHostRole): string {
  switch (role) {
    case 'Api': return 'API'
    case 'Automaton': return 'Automaton'
    case 'Dashboard': return 'Hangfire dashboard'
    default: return role
  }
}

/**
 * A host as a reader names it: "API (this instance)", "Automaton on WORKER-01",
 * "Automaton" when no machine has reported.
 */
export function hostLabel(host: {
  role: ConfigurationHostRole
  hostName: string | null
  isThisInstance: boolean
}): string {
  if (host.isThisInstance) return `${roleLabel(host.role)} (this instance)`
  return host.hostName ? `${roleLabel(host.role)} on ${host.hostName}` : roleLabel(host.role)
}

/**
 * A host's standing on the page, and a status role only for the one state that
 * is a fault in the REPORTING.
 *
 * Read in a fixed order. `hasPublisher` first, because the dashboard is served as
 * never-reported too and "never reported" would suggest it might one day; it
 * cannot, nothing publishes it. Then this instance, which is read directly and has
 * no projection to age. Then never-reported **before** stale, for the reason the
 * workers and sources pages give: an empty projection has no age, and asking
 * about staleness first renders it as fresh.
 *
 * A publishing host gets no green. Its settings are not "good" for being
 * reported, and a green badge in a column of `false` reads as a verdict on them.
 *
 * `isUnmeasured` is separate from a null `status`, because the two are not the
 * same claim. Only the two states with no reading behind them take the dash;
 * "Publishing" and "Read from this process" are known facts with no status role,
 * and a dash beside them would say nobody knows — the dash rule inverted.
 */
export function hostStanding(host: ConfigurationHost): {
  label: string
  status: StatusRole | null
  isUnmeasured: boolean
} {
  if (!host.hasPublisher) return { label: 'Not observable', status: null, isUnmeasured: true }
  if (host.isThisInstance) return { label: 'Read from this process', status: null, isUnmeasured: false }
  if (host.hasNeverReported) return { label: 'Never reported', status: null, isUnmeasured: true }
  if (host.isStale) return { label: 'Stopped publishing', status: 'warning', isUnmeasured: false }
  return { label: 'Publishing', status: null, isUnmeasured: false }
}

/**
 * The line under a host's role: its machine, or what stands in for one.
 *
 * The dashboard and a never-reported Automaton both arrive with no machine name,
 * for different reasons — nothing CAN publish the first, and nothing HAS
 * published the second — and one caption for both would promise the dashboard a
 * reading that is never coming.
 */
export function hostIdentity(host: ConfigurationHost): { text: string; isMachineName: boolean } {
  if (host.hostName) return { text: host.hostName, isMachineName: true }
  if (host.isThisInstance) return { text: 'this instance', isMachineName: false }
  if (!host.hasPublisher) return { text: 'nothing publishes it', isMachineName: false }
  return { text: 'no machine has reported', isMachineName: false }
}

/** "A", "A or B", "A, B or C" — for naming the hosts that do not read a key. */
export function joinOr(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`
}

/**
 * Where the reading came from, said as of process start.
 *
 * Every line begins from the same fact — the value was read when the process
 * started — because nothing here is runtime-mutable and a configuration page
 * that implied live values would be lying about when they took effect. `NotSet`
 * is worded as doc 16 §7 found it: stated in no configuration source at all.
 *
 * Returns null for a never-reported reading, which has no source to state.
 */
export function sourceLabel(observation: ConfigurationObservation): string | null {
  if (observation.hasNeverReported || observation.source == null) return null

  const where = observation.source === 'NotSet'
    ? 'stated in no configuration source'
    : `read at start from ${sourceKindLabel(observation.source, observation.sourceFile)}`

  // A list-shaped setting's items can come from different providers, and for
  // `ApiKeys` that difference is the finding: a second slot from a file is the
  // same hole as the first.
  if (observation.itemSources.length === 0) return where
  const items = observation.itemSources.map((s) => sourceKindLabel(s, null)).join(', ')
  return `${where}; entries from ${items}`
}

/** A provider type, named. Never a provider's own description. */
export function sourceKindLabel(kind: ConfigurationSourceKind, sourceFile: string | null): string {
  switch (kind) {
    case 'NotSet': return 'nowhere (code default)'
    case 'AppSettingsJson': return sourceFile ?? 'an appsettings file'
    case 'UserSecrets': return 'user secrets'
    case 'EnvironmentVariables': return 'environment variables'
    case 'CommandLine': return 'the command line'
    case 'InMemory': return 'an in-memory provider added by code'
    case 'KeyVault': return 'Key Vault'
    case 'HostEnvironment': return 'the host environment'
    case 'Other': return 'an unrecognised provider (treated as a secret store)'
    case 'Unknown': return 'an undetermined source (treated as a secret store)'
    default: return kind
  }
}

/** Section heading per tier. */
export function tierTitle(tier: ConfigurationTier): string {
  switch (tier) {
    case 'Locked': return 'Locked — shown, deliberately not editable'
    case 'Operational': return 'Operational — shown, read-only in this version'
    case 'Bootstrap': return 'Bootstrap — presence only, never a value'
    default: return tier
  }
}

/** What a tier means for what the page shows, in one or two sentences. */
export function tierDescription(tier: ConfigurationTier): string {
  switch (tier) {
    case 'Locked':
      return 'Security controls and switches fixed when services are registered. These are the settings most '
        + 'worth seeing and least safe to edit, so each row says why it is locked.'
    case 'Operational':
      return 'Retention windows, thresholds and behavioural flags. The value shown is the parsed, effective '
        + 'one — the configured value, or the code default when nothing sets it.'
    case 'Bootstrap':
      return 'Anything needed to authenticate or reach the app, and anything whose value could carry a '
        + 'credential: connection strings, URLs, keys, ports. The page says whether each is set and which '
        + 'source supplied it, and never shows a value — a code default included.'
    default:
      return ''
  }
}

/**
 * Settings grouped by tier, in page order, each group keeping the server's order.
 *
 * A tier this app does not know is given a group of its own after the three,
 * never dropped. The vocabulary is fixed server-side and pinned by a test, so
 * this should never fire — but a page built to stop settings being invisible
 * must not be the thing that hides one after a rename.
 */
export function groupByTier(
  settings: ConfigurationSetting[],
): { tier: ConfigurationTier; settings: ConfigurationSetting[] }[] {
  const known = TIER_ORDER
    .map((tier) => ({ tier, settings: settings.filter((s) => s.tier === tier) }))
    .filter((group) => group.settings.length > 0)

  const unknownTiers = [...new Set(settings.map((s) => s.tier).filter((t) => !TIER_ORDER.includes(t)))]
  return [
    ...known,
    ...unknownTiers.map((tier) => ({ tier, settings: settings.filter((s) => s.tier === tier) })),
  ]
}

/**
 * The editability note a whole group shares, or null when rows differ.
 *
 * Operational and Bootstrap rows carry one sentence each today, identical down
 * the tier, and printing it sixty times would bury the rationale it sits beside.
 * Locked rows each carry their own reason. Measured rather than assumed per tier,
 * so a row that gains its own note is printed on that row the day it does.
 */
export function sharedEditabilityNote(settings: ConfigurationSetting[]): string | null {
  const notes = new Set(settings.map((s) => s.editabilityNote))
  return notes.size === 1 ? settings[0].editabilityNote : null
}

/** "Must agree across hosts", "Exactly one host drains". */
export function crossHostKindLabel(kind: ConfigurationCrossHostCheckKind): string {
  switch (kind) {
    case 'MustAgree': return 'Must agree across hosts'
    case 'ExactlyOneDrainer': return 'Exactly one host may drain'
    default: return kind
  }
}

/**
 * A verdict's status role, or null for a rule the server could not judge.
 *
 * `null` is not agreement. It is the server declining to compare because a host
 * has not reported, is stale, or withholds its value — and it takes the dash,
 * never a green, which would say two hosts agree when nobody has looked.
 */
export function crossHostStatus(check: ConfigurationCrossHostCheck): { status: StatusRole | null; label: string } {
  if (check.holds == null) return { status: null, label: 'Not judged' }
  return check.holds
    ? { status: 'good', label: 'Holds' }
    : { status: 'critical', label: 'Does not hold' }
}

/**
 * "3 hold · 1 does not · 2 not judged" — a count of served verdicts, never a
 * rate. A proportion of rules holding would divide by the rules that could be
 * judged, which is zero on the ordinary day Automaton has not reported, and the
 * dash rule applies to the header as much as to the rows.
 */
export function crossHostTally(checks: ConfigurationCrossHostCheck[]): string {
  const holds = checks.filter((c) => c.holds === true).length
  const fails = checks.filter((c) => c.holds === false).length
  const unjudged = checks.filter((c) => c.holds == null).length
  const parts: string[] = []
  if (holds > 0) parts.push(`${holds} hold${holds === 1 ? 's' : ''}`)
  if (fails > 0) parts.push(`${fails} ${fails === 1 ? 'does' : 'do'} not`)
  if (unjudged > 0) parts.push(`${unjudged} not judged`)
  return parts.join(' · ')
}

/**
 * Whether the API answering this request reports running in Development.
 *
 * That is the one environment in which the gate on this page degrades — the
 * admin policy becomes "any authenticated user", and the checked-in dev API key
 * satisfies it — so the page states the trade when, and only when, it applies.
 * Read from the API's OWN served observation, never from this SPA's build mode:
 * the gate is the API's, and a deployed SPA pointed at a Development API is the
 * case a reader could not otherwise see.
 *
 * Reads `value` only for a Locked `Shown` observation, which the server produces
 * by matching one of three closed-set names; it is compared, never printed. An
 * absent key or a withheld value yields false — the banner is supplementary, and
 * no reading is lost without it, since the environment row itself is in the
 * Locked table.
 */
export function apiRunsInDevelopment(settings: ConfigurationSetting[]): boolean {
  const environment = settings.find((s) => s.key === 'ASPNETCORE_ENVIRONMENT')
  const own = environment?.observations.find((o) => o.isThisInstance)
  return own != null && own.disposition === 'Shown' && own.value === 'Development'
}
